/**
 * A tab that was opened before a deploy holds an `index.html` naming chunks
 * that the deploy has replaced. The shell keeps working until the user opens
 * a screen it has not loaded yet — then its dynamic import asks for a file
 * that is gone, and the screen simply never appears.
 *
 * The fix is the one the browser cannot take by itself: load the address
 * again, which fetches the current shell with the current chunk names. Once
 * per address, so a chunk that is missing for any other reason cannot put the
 * page into a reload loop.
 */
const MARKER = 'eua:stale-chunk'

/**
 * The wording differs per engine; all three mean the same missing file.
 * Chrome: `Failed to fetch dynamically imported module`, Firefox: `error
 * loading dynamically imported module`, Safari: `Importing a module script
 * failed`.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /dynamically imported module|Importing a module script failed/i.test(message)
}

export interface StaleChunkDeps {
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  reload: (path: string) => void
}

/** Returns whether the reload was started. */
export function recoverFromStaleChunk(error: unknown, path: string, deps: StaleChunkDeps): boolean {
  if (!isStaleChunkError(error)) return false
  if (deps.storage.getItem(MARKER) === path) return false
  deps.storage.setItem(MARKER, path)
  deps.reload(path)
  return true
}

/** A navigation that arrived: whatever was stale has been loaded again. */
export function forgetStaleChunk(deps: Pick<StaleChunkDeps, 'storage'>): void {
  deps.storage.removeItem(MARKER)
}
